import { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";

/**
 * KeyboardShortcuts
 * ========================================
 * Floating help panel showing all keyboard shortcuts
 * Triggered by pressing '?' key
 */
export default function KeyboardShortcuts() {
  const { theme } = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Show on '?' key
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setVisible(true);
      }
      // Hide on 'Escape'
      if (e.key === "Escape" && visible) {
        setVisible(false);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [visible]);

  if (!visible) return null;

  const shortcuts = [
    { category: "Navigation", items: [
      { key: "ESC", action: "Exit fullscreen / Unfocus camera" },
      { key: "F", action: "Toggle fullscreen" },
      { key: "?", action: "Show/hide this help" },
    ]},
    { category: "Layouts", items: [
      { key: "1", action: "Spotlight view" },
      { key: "2", action: "2×2 Grid" },
      { key: "3", action: "3×3 Grid" },
      { key: "4", action: "4×4 Grid" },
      { key: "L", action: "List view" },
    ]},
    { category: "Tour Mode", items: [
      { key: "T", action: "Toggle auto-tour" },
      { key: "Space", action: "Pause/Resume tour" },
      { key: "→", action: "Next camera" },
      { key: "←", action: "Previous camera" },
    ]},
    { category: "Actions", items: [
      { key: "Double Click", action: "Open camera detail" },
      { key: "Click", action: "Focus camera" },
    ]},
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={() => setVisible(false)}
      />

      {/* Panel */}
      <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl rounded-2xl shadow-2xl border overflow-hidden ${
        theme === "dark"
          ? "bg-neutral-800 border-neutral-700"
          : "bg-white border-gray-200"
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          theme === "dark" ? "border-neutral-700" : "border-gray-200"
        }`}>
          <div>
            <h2 className={`text-xl font-bold ${
              theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}>
              Keyboard Shortcuts
            </h2>
            <p className={`text-sm mt-1 ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            }`}>
              Press <kbd className="px-2 py-1 rounded bg-blue-500 text-white text-xs font-mono">?</kbd> to toggle this panel
            </p>
          </div>
          <button
            onClick={() => setVisible(false)}
            className={`p-2 rounded-lg transition ${
              theme === "dark"
                ? "hover:bg-neutral-700 text-gray-400"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Shortcuts Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[60vh] overflow-y-auto">
          {shortcuts.map((category) => (
            <div key={category.category}>
              <h3 className={`text-sm font-bold mb-3 uppercase tracking-wider ${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}>
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <kbd className={`px-3 py-1.5 rounded-lg font-mono text-sm font-semibold shadow-sm ${
                      theme === "dark"
                        ? "bg-neutral-700 text-gray-200 border border-neutral-600"
                        : "bg-gray-100 text-gray-900 border border-gray-300"
                    }`}>
                      {item.key}
                    </kbd>
                    <span className={`flex-1 text-sm ${
                      theme === "dark" ? "text-gray-300" : "text-gray-700"
                    }`}>
                      {item.action}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${
          theme === "dark"
            ? "border-neutral-700 bg-neutral-900/50"
            : "border-gray-200 bg-gray-50"
        }`}>
          <p className={`text-xs ${
            theme === "dark" ? "text-gray-400" : "text-gray-500"
          }`}>
            💡 <span className="font-semibold">Pro tip:</span> Most shortcuts work in any view mode for quick navigation
          </p>
        </div>
      </div>
    </>
  );
}
